import cuid from 'cuid'
import Storage from 'disco-storage'
import Cookies from 'js-cookie'
import omit from 'lodash/omit'
import retry from 'retry'
import request from 'superagent'
import { __REACT_NATIVE__, userTokenKey } from '@disco/constants/environment'
import { JSON_HEADERS, RETRIABLE_ERRORS, RETRY_OPTIONS } from './constants'
import type { HttpErrorType } from 'types/errors'
import errorHandler, { HttpError } from './error-handler'
type RequestType = 'get' | 'put' | 'post' | 'patch' | 'del'
type ParamsType = Record<string, any> | Array<Record<string, any>>
type AllowedHeaders =
  | 'Accept'
  | 'X-Disco-GroupIDs'
  | 'X-Disco-ClientID'
  | 'X-CSRFToken'
  | 'Authorization'
type RequestHeaders = Partial<Record<AllowedHeaders, string>>
export type RequestFields = Record<string, any>
export type AttachedFile = {
  field: string
  file: string | Blob | Buffer | File | null
  filename?: string
}
export type GetGroupsParams = {
  since?: string | null | undefined
  include_shared_channels?: boolean | null | undefined
}
export type RequestOptions = {
  headers?: RequestHeaders
  fields?: RequestFields
  attachments?: AttachedFile[]
  progressEvent?: (e: any) => any
  skipAuth?: boolean
  skipClientIdentification?: boolean
  abortSignal?: AbortSignal
}
type GetRequestPrimitiveConfig = {
  url: string
  requestType: RequestType
  params: ParamsType | null | undefined
  headers: RequestHeaders
  passCredentials?: boolean
  additionalRequestOptions?: RequestOptions | null | undefined
}
export type PromiseType<T> = Promise<T>
type SuperagentRequest = typeof request.Request

const getRequestPromise = (req: SuperagentRequest, actionInfo: string) => {
  return new Promise<any>((resolve, reject) => {
    req
      .then(response => {
        return resolve(response.body || response.text)
      })
      .catch(error => {
        const httpError = error.response
          ? new HttpError(
              error.message,
              error.response.status,
              error.response.body,
            )
          : error

        if (__REACT_NATIVE__ && recoverableSessionError(httpError)) {
          reject(httpError)
        }

        errorHandler(httpError, actionInfo, reject)
      })
  })
}

const getRetriableRequestPromise = (
  req: SuperagentRequest,
  actionInfo: string,
) => {
  return new Promise<any>((resolve, reject) => {
    const operation = retry.operation(RETRY_OPTIONS)
    operation.attempt(async () => {
      try {
        const result = await getRequestPromise(req, actionInfo)
        resolve(result)
      } catch (error) {
        if (!RETRIABLE_ERRORS.includes(error.status)) {
          reject(error) // May need this if we upgrade superagent as aborting throws an error
          // Currently aborting is silent
          // } else if (error.message === 'Aborted') {
          //   reject(error)
        } else if (!operation.retry(error)) {
          reject(operation.mainError() || error)
        }
      }
    })
  })
}

export let browserInstanceId: string = cuid()

const recoverableSessionError = (error: HttpErrorType) =>
  error.status === 403 &&
  error.body &&
  error.body.detail === 'Token has expired.'

export default class BaseAPI {
  host: string
  csrfToken: string | null | undefined
  jwtToken: string | null | undefined
  deviceToken: string | null | undefined
  messagingToken: string | null | undefined
  visitorId: string | null | undefined

  constructor(token?: string) {
    this.csrfToken = token
    this.host =
      (typeof window !== 'undefined' && window.host) ||
      (typeof global !== 'undefined' && global.host) ||
      process.env.HOST ||
      ''

    // @ts-expect-error hot module reloading is not supported in Node.js
    if (!token && module.hot) {
      // @ts-expect-error hot module reloading is not supported in Node.js
      module.hot.accept(() => {
        Storage.get(userTokenKey).then(storedToken => {
          if (storedToken) {
            this.jwtToken = storedToken.jwttoken
            this.csrfToken = storedToken.csrftoken
          }
        })
      })
    }
  }

  get browserInstanceId(): string {
    return browserInstanceId
  }

  resetBrowserInstanceId: () => void = () => (browserInstanceId = cuid())
  getHeaders: (
    headers: RequestHeaders,
    requestType: RequestType,
    passCredentials: boolean,
    additionalRequestOptions?: RequestOptions | null | undefined,
  ) => RequestHeaders = (
    headers,
    requestType,
    passCredentials,
    additionalRequestOptions,
  ) => {
    const result = { ...headers }

    if (__REACT_NATIVE__) {
      if (
        passCredentials &&
        this.jwtToken &&
        !additionalRequestOptions?.skipAuth
      )
        result['Authorization'] = `JWT ${this.jwtToken}`
      if (this.deviceToken) result['X-Disco-ClientID'] = this.deviceToken
    } else {
      if (requestType !== 'get' && passCredentials) {
        result['X-Disco-ClientID'] = browserInstanceId
        const csrfToken = this.readCsrfToken()
        if (csrfToken) result['X-CSRFToken'] = csrfToken
      }
    }

    if (additionalRequestOptions?.skipClientIdentification)
      return omit(result, 'X-Disco-ClientID')
    return result
  }
  readCsrfToken: () => string | null | undefined = () => {
    if (__REACT_NATIVE__) return null
    const csrfToken = Cookies.get('csrftoken')

    if (csrfToken) {
      return csrfToken
    }

    // TODO: move to datadog or delete? Sentry.metrics.increment('csrf_via_api', 1)
    return this.csrfToken
  }
  // warning, usage of this method in inherited class leaves you responsible
  // to handling retry if request fails due to 403 error(token expired)
  _getRequestPrimitive: (
    config: GetRequestPrimitiveConfig,
  ) => SuperagentRequest = ({
    url,
    requestType,
    params,
    headers,
    passCredentials = false,
    additionalRequestOptions,
  }) => {
    const req = request[requestType](url)

    if (requestType === 'get') {
      if (params) req.query(params)
    } else {
      if (params) req.send(params)
    }

    const resultHeaders = this.getHeaders(
      headers,
      requestType,
      passCredentials,
      additionalRequestOptions,
    )
    req.set(resultHeaders)

    if (additionalRequestOptions) {
      const { fields, attachments, progressEvent, abortSignal } =
        additionalRequestOptions

      if (fields) {
        Object.keys(fields).forEach(key => {
          req.field(key, fields[key])
        })
      }

      if (attachments) {
        attachments.forEach(attachment => {
          const { field, file, filename } = attachment
          req.attach(field, file, filename)
        })
      }

      if (progressEvent) {
        req.on('progress', progressEvent)
      }

      if (abortSignal) {
        abortSignal.addEventListener('abort', () => {
          req.abort()
        })
      }
    }

    if (!__REACT_NATIVE__ && passCredentials) req.withCredentials()
    return req
  }

  refreshTokenAndRetry(
    config: GetRequestPrimitiveConfig,
    actionInfo: string,
    error: HttpErrorType,
  ): Promise<any> {
    if (recoverableSessionError(error)) {
      // update token
      return getRetriableRequestPromise(
        this._getRequestPrimitive({
          url: `${this.host}/api-auth/token-refresh/`,
          requestType: 'post',
          params: {
            token: this.jwtToken,
          },
          headers: JSON_HEADERS,
          passCredentials: false,
        }),
        'api.tokenRefresh',
      )
        .then(result => {
          if (result.token) {
            this.jwtToken = result.token
            Storage.set(userTokenKey, {
              jwttoken: result.token,
            })
            return getRetriableRequestPromise(
              this._getRequestPrimitive(config),
              actionInfo,
            )
          }

          throw error
        })
        .catch(refreshError => {
          // throw error that token cannot be refreshed, we need to throw user to login screen after that
          throw refreshError
        })
    } else {
      throw error
    }
  }

  // generic methods
  getUrl<T>(
    url: string,
    params: ParamsType | null | undefined,
    actionInfo: string = 'api.getUrl',
    requestOptions?: RequestOptions,
  ): Promise<T> {
    if (!url || url.trim() === '')
      return Promise.reject(new Error('Invalid url'))
    const headers = requestOptions?.headers ?? JSON_HEADERS
    const passCredentials = !url.match(/.json$/)
    const config: GetRequestPrimitiveConfig = {
      url,
      requestType: 'get',
      params,
      headers,
      passCredentials,
      additionalRequestOptions: requestOptions,
    }
    const promise = getRetriableRequestPromise(
      this._getRequestPrimitive(config),
      actionInfo,
    )
    if (__REACT_NATIVE__)
      return promise.catch((error: HttpErrorType) =>
        this.refreshTokenAndRetry(config, actionInfo, error),
      )
    else return promise
  }

  patchUrl<T>(
    url: string,
    params: ParamsType | null | undefined,
    actionInfo: string = 'api.patchUrl',
    requestOptions?: RequestOptions,
  ): Promise<T> {
    if (!url || url.trim() === '')
      return Promise.reject(new Error('Invalid url'))
    const headers = requestOptions?.headers ?? JSON_HEADERS
    const config: GetRequestPrimitiveConfig = {
      url,
      requestType: 'patch',
      params,
      headers,
      passCredentials: true,
      additionalRequestOptions: requestOptions,
    }
    const promise = getRetriableRequestPromise(
      this._getRequestPrimitive(config),
      actionInfo,
    )
    if (__REACT_NATIVE__)
      return promise.catch((error: HttpErrorType) =>
        this.refreshTokenAndRetry(config, actionInfo, error),
      )
    else return promise
  }

  putUrl<T>(
    url: string,
    params: ParamsType | null | undefined,
    actionInfo: string = 'api.putUrl',
    requestOptions?: RequestOptions,
  ): Promise<T> {
    if (!url || url.trim() === '')
      return Promise.reject(new Error('Invalid url'))
    const headers = requestOptions?.headers ?? JSON_HEADERS
    const config: GetRequestPrimitiveConfig = {
      url,
      requestType: 'put',
      params,
      headers,
      passCredentials: true,
      additionalRequestOptions: requestOptions,
    }
    const promise = getRetriableRequestPromise(
      this._getRequestPrimitive(config),
      actionInfo,
    )
    if (__REACT_NATIVE__)
      return promise.catch((error: HttpErrorType) =>
        this.refreshTokenAndRetry(config, actionInfo, error),
      )
    else return promise
  }

  postUrl<T>(
    url: string,
    params: ParamsType | null | undefined,
    actionInfo: string = 'api.postUrl',
    requestOptions?: RequestOptions | null | undefined,
  ): Promise<T> {
    if (!url || url.trim() === '')
      return Promise.reject(new Error('Invalid url'))
    const headers = requestOptions?.headers ?? JSON_HEADERS
    const config: GetRequestPrimitiveConfig = {
      url,
      requestType: 'post',
      params,
      headers,
      passCredentials: !(requestOptions?.skipAuth ?? false),
      additionalRequestOptions: requestOptions,
    }
    const promise = getRetriableRequestPromise(
      this._getRequestPrimitive(config),
      actionInfo,
    )
    if (__REACT_NATIVE__)
      return promise.catch((error: HttpErrorType) =>
        this.refreshTokenAndRetry(config, actionInfo, error),
      )
    else return promise
  }

  delUrl<T>(
    url: string,
    actionInfo: string = 'api.delUrl',
    params?: ParamsType | null | undefined,
  ): Promise<T> {
    if (!url || url.trim() === '')
      return Promise.reject(new Error('Invalid url'))
    const config: GetRequestPrimitiveConfig = {
      url,
      requestType: 'del',
      params,
      headers: JSON_HEADERS,
      passCredentials: true,
    }
    const promise = getRetriableRequestPromise(
      this._getRequestPrimitive(config),
      actionInfo,
    )
    if (__REACT_NATIVE__)
      return promise.catch((error: HttpErrorType) =>
        this.refreshTokenAndRetry(config, actionInfo, error),
      )
    else return promise
  }
}
